import React, { useMemo, useState } from 'react'
import { generatePath } from 'react-router-dom'
import { DataCard, DataColumn, DataGrid } from '@/components/DataCard/DataCard'
import {
  FormCheckboxField,
  FormMultiSelectField,
  FormReadonlyField,
  FormSelectField,
} from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import { ButtonGroup } from '@/components/ui/button-group'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Spinner } from '@/components/ui/spinner'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { DemoAccountDetails, UpdateDemoTradingAccount } from 'api/Clients/Types/DemoAccountDetails'
import { TradingAccountStatus } from 'api/Clients/Types/TradingAccount'
import { WelcomeAccountDetails } from 'api/Clients/Types/WelcomeAccountDetails'
import { NameDto } from 'api/Common/Types'
import { PermissionDto } from 'api/Identity/Types/PermissionDto'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { AccountGroupBasicDto } from 'api/Trading-Accounts/Types/AccountGroupDto'
import {
  isAccountGroupDemo,
  isAccountGroupWelcome,
} from 'api/Trading-Accounts/Types/AccountGroupType'
import { LeverageType } from 'api/Trading-Accounts/Types/LeverageType'
import { PlatformTypeId } from 'api/Trading-Accounts/Types/PlatformTypeDto'
import { RestrictionDto } from 'api/Wallet/Types/RestrictionDto'
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal'
import { Modal } from 'components/CustomModal'
import { Form, FormikProps, withFormik } from 'formik'
import { ChevronDown, Info } from 'lucide-react'
import { formatLocalDateUTC } from 'utils/date'
import { links } from 'utils/featureToggles'
import { FormSubmitValues } from 'utils/formValidation'
import { hasPermission } from 'utils/permissionsValidator'

import { LeadWelcomeAccountsActivity } from '../../../Leads/Details/LeadWelcomeAccountsTable/LeadWelcomeAccountsActivity'
import { LeadWelcomeAccountsAnalytics } from '../../../Leads/Details/LeadWelcomeAccountsTable/LeadWelcomeAccountsAnalytics'
import { AccountGroupDetailsBlock } from './AccountGroupDetailsBlock'

interface DemoWelcomeAccountDetailsFormValues {
  name: string
  accountGroupId: string
  leverageType: LeverageType
  server: string
  statusTypeId: number
  currency: string
  lead: {
    id: string
    name: string
  }
  created: string
  externalId: string
  tickmillCompany: string
  platformType: string
  tradingAccountGroupType: string
  accountGroupType: string
  bookType?: string
  classification: string
  country?: string
  isEnabled: boolean
  isSwapFree: boolean
  canBeAttached: boolean
  canBeDirectAssigned: boolean
  canBeIBAssigned: boolean
  accountGroupRestrictions: number[]
  isTradingViewConnected: boolean | null
}

export enum AccountFundsType {
  AddFunds = 'ADD_FUNDS',
  DeductFunds = 'DEDUCT_FUNDS',
}

export const isAddFunds = (accountFundsType: AccountFundsType): boolean => {
  return AccountFundsType.AddFunds === accountFundsType
}

const uniqueLeverageTypes = (leverageTypes: LeverageType[]): LeverageType[] =>
  Array.from(leverageTypes)
    .map((x) => ({ id: Number(x.id), name: x.name }))
    .reduce((acc: LeverageType[], current) => {
      if (!acc.find((x) => x.id === current.id)) {
        return acc.concat([current])
      }

      return acc
    }, [])

const sortLeverageTypes = (f1: LeverageType, f2: LeverageType): number => {
  const [, name1 = '0'] = f1.name.split(':')
  const [, name2 = '0'] = f2.name.split(':')

  return Number.parseInt(name1, 10) - Number.parseInt(name2, 10)
}

const StatusWarningBadge = () => (
  <Tooltip>
    <TooltipTrigger>
      <Info size={14} style={{ marginLeft: '0.25rem' }} />
    </TooltipTrigger>
    <TooltipContent side='top'>Trading account status is not approved.</TooltipContent>
  </Tooltip>
)

const DemoWelcomeAccountDetailsFormUI: React.FC<
  FormikProps<DemoWelcomeAccountDetailsFormValues> & DemoWelcomeAccountDetailsFormProps
> = (props) => {
  const {
    data,
    permissions,
    canResetAccountPassword,
    canEditTradingAccount,
    resetForm,
    handleSubmit,
    isEdit,
    setEdit,
    setAuditLogModal,
    onDisqualify,
  } = props

  const [isDisqualifyModalOpen, setDisqualifyModalOpen] = useState<boolean>(false)
  const [activityModal, setActivityModal] = useState<{
    data?: DemoAccountDetails | WelcomeAccountDetails
    visible: boolean
  }>({ data: undefined, visible: false })
  const availableLeverageTypes = useMemo(
    () =>
      uniqueLeverageTypes([...props.leverageType, props.values.leverageType]).sort(
        sortLeverageTypes
      ),
    [props.leverageType, props.values.leverageType]
  )
  const selectedAccountGroup = useMemo(
    () =>
      props.accountGroups.find((accountGroup) => accountGroup.id === props.values.accountGroupId) ??
      props.data.accountGroup,
    [props.accountGroups, props.data.accountGroup, props.values.accountGroupId]
  )
  const isStatusNotApproved = Number(props.values.statusTypeId) !== TradingAccountStatus.Approved
  const statusRightAddon = isStatusNotApproved ? <StatusWarningBadge /> : undefined

  const handleSubmitForm = () => {
    props.setSubmitting(true)
    setEdit(false)
    handleSubmit()
  }

  const handleCancel = () => {
    setEdit(false)
    resetForm()
  }

  const handleResetMasterPassword = async () => {
    await props.resetPassword({ withInvestorPassword: false })
  }

  const handleResetInvestorPassword = async () => {
    await props.resetPassword({ withInvestorPassword: true })
  }

  const handleOpenAddDeductFunds = (fundsType: AccountFundsType) => {
    props.onOpenAddDeductFunds?.(fundsType)
  }

  const handleActivityOpenModal = (
    welcomeTradingAccount: DemoAccountDetails | WelcomeAccountDetails
  ) => {
    setActivityModal({ data: welcomeTradingAccount, visible: true })
  }

  const handleActivityCloseModal = () => {
    setActivityModal({ data: undefined, visible: false })
  }

  const handleDisqualify = () => {
    if (onDisqualify) {
      onDisqualify()
    }
  }

  const handleDisqualifyModalOpen = () => {
    setDisqualifyModalOpen(true)
  }

  const handleDisqualifyModalClose = () => {
    setDisqualifyModalOpen(false)
  }

  const renderMenu = () => {
    return (
      <>
        <DropdownMenuItem onClick={() => setAuditLogModal(true)} data-test='audit-log-menu-button'>
          Audit Log
        </DropdownMenuItem>
        <DropdownMenuItem
          disabled={!hasPermission(permissions, canResetAccountPassword)}
          onClick={handleResetMasterPassword}
          data-test='reset-master-password-menu-button'
        >
          Reset Master Password
        </DropdownMenuItem>
        {data.accountGroup.platformType.id !== PlatformTypeId.TickmillTrader && (
          <DropdownMenuItem
            disabled={!hasPermission(permissions, canResetAccountPassword)}
            onClick={handleResetInvestorPassword}
            data-test='reset-investor-password-menu-button'
          >
            Reset Investor Password
          </DropdownMenuItem>
        )}
        {isAccountGroupWelcome(props.data.accountGroup.accountGroupType.id) && (
          <DropdownMenuItem
            disabled={
              !hasPermission(permissions, PermissionsEnum.CanDeductFundsFromWelcomeAccounts)
            }
            onClick={() => handleOpenAddDeductFunds(AccountFundsType.DeductFunds)}
          >
            Deduct Funds
          </DropdownMenuItem>
        )}
        {isAccountGroupDemo(props.data.accountGroup.accountGroupType.id) && (
          <DropdownMenuItem
            disabled={!hasPermission(permissions, PermissionsEnum.CanAddFundsToDemoAccounts)}
            onClick={() => handleOpenAddDeductFunds(AccountFundsType.AddFunds)}
            data-test='add-funds-menu-button'
          >
            Add funds
          </DropdownMenuItem>
        )}
        {isAccountGroupDemo(props.data.accountGroup.accountGroupType.id) && (
          <DropdownMenuItem
            disabled={!hasPermission(permissions, PermissionsEnum.CanDeductFundsFromDemoAccounts)}
            onClick={() => handleOpenAddDeductFunds(AccountFundsType.DeductFunds)}
            data-test='deduct-funds-menu-button'
          >
            Deduct funds
          </DropdownMenuItem>
        )}
        {isAccountGroupWelcome(props.data.accountGroup.accountGroupType.id) && (
          <DropdownMenuItem
            onClick={() => handleActivityOpenModal(data)}
            data-test='detailed-analytics-menu-button'
          >
            Activity
          </DropdownMenuItem>
        )}
        {isAccountGroupWelcome(props.data.accountGroup.accountGroupType.id) && (
          <DropdownMenuItem onClick={handleDisqualifyModalOpen} data-test='disqualify-menu-button'>
            Disqualify
          </DropdownMenuItem>
        )}
      </>
    )
  }

  return (
    <React.Fragment>
      {isDisqualifyModalOpen && (
        <ConfirmationModal
          contentText='Do you want to proceed with disqualifying?'
          yesButtonText='Proceed'
          onYes={handleDisqualify}
          onCancel={handleDisqualifyModalClose}
          visible={isDisqualifyModalOpen}
        />
      )}
      {activityModal?.data && (
        <Modal width={1100} visible={activityModal.visible} onClose={handleActivityCloseModal}>
          <div>
            <LeadWelcomeAccountsActivity data={activityModal.data} />
          </div>
          <div>
            <LeadWelcomeAccountsAnalytics data={activityModal.data} />
          </div>
        </Modal>
      )}
      <Form>
        <DataGrid
          rightAddon={
            <ButtonGroup>
              {!isEdit ? (
                <Button
                  type='button'
                  disabled={
                    props.isSubmitting || !hasPermission(permissions, canEditTradingAccount)
                  }
                  onClick={() => setEdit(true)}
                  data-test='edit-button'
                >
                  Edit
                </Button>
              ) : (
                <>
                  <Button
                    variant='outline'
                    type='button'
                    onClick={handleCancel}
                    disabled={props.isSubmitting}
                    data-test='cancel-button'
                  >
                    Cancel
                  </Button>
                  <Button
                    type='button'
                    onClick={handleSubmitForm}
                    disabled={props.isSubmitting}
                    data-test='save-button'
                  >
                    Save
                    {props.isSubmitting && <Spinner data-icon='inline-start' />}
                  </Button>
                </>
              )}

              <DropdownMenu>
                <DropdownMenuTrigger
                  render={
                    <Button
                      type='button'
                      variant='outline'
                      size='icon'
                      aria-label='More options'
                      data-test='more-button'
                      disabled={props.isSubmitting}
                    />
                  }
                >
                  <ChevronDown />
                </DropdownMenuTrigger>
                <DropdownMenuContent align='end'>{renderMenu()}</DropdownMenuContent>
              </DropdownMenu>
            </ButtonGroup>
          }
        >
          <DataColumn>
            <DataCard columns={2} title='Main'>
              <FormReadonlyField
                label='Lead'
                value={props.values.lead.name}
                link={generatePath(links.recordsLeads.detailsUrl as string, {
                  id: props.values.lead.id,
                  slug: 'details',
                })}
              />

              <FormSelectField
                options={props.accountGroups}
                name='accountGroupId'
                label='Account Group'
                readOnly={!isEdit}
                onChange={(value) => props.setFieldValue('accountGroupId', value)}
                link={generatePath(links.recordsAccountGroups.detailsUrl as string, {
                  id: selectedAccountGroup.id,
                  slug: 'details',
                })}
              />

              <FormReadonlyField label='Date Created' name='created' value={props.values.created} />

              <FormSelectField
                options={availableLeverageTypes}
                label='Leverage'
                readOnly={!isEdit}
                name='leverageType'
              />

              <FormReadonlyField
                label='External ID'
                name='externalId'
                value={props.values.externalId}
              />

              <FormSelectField
                options={props.tradingAccountStatus}
                label='Status'
                name='statusTypeId'
                readOnly={!isEdit}
                onChange={(value) => props.setFieldValue('statusTypeId', Number(value))}
                rightAddon={statusRightAddon}
              />
            </DataCard>

            <DataCard columns={2} title='Platform Details'>
              <FormReadonlyField label='Server' name='server' value={props.values.server} />
              <FormReadonlyField
                label='Platform'
                name='platformType'
                value={props.values.platformType}
              />
              <FormCheckboxField
                label='TV'
                value={Boolean(props.values.isTradingViewConnected)}
                readOnly
              />
            </DataCard>
          </DataColumn>

          <DataColumn>
            <AccountGroupDetailsBlock />

            <DataCard title='Account Group Restrictions'>
              <FormMultiSelectField
                options={props.tradingAccountRestrictions}
                label='Restrictions'
                showSearch
                name='accountGroupRestrictions'
                placeholder='No restrictions'
                readOnly
              />
            </DataCard>
          </DataColumn>
        </DataGrid>
      </Form>
    </React.Fragment>
  )
}

function dataToSubmit(
  values: DemoWelcomeAccountDetailsFormValues
): FormSubmitValues<UpdateDemoTradingAccount> {
  return {
    accountGroupId: values.accountGroupId,
    leverageTypeId: values.leverageType.id,
    tradingAccountStatusId: Number(values.statusTypeId),
  }
}

export interface DemoWelcomeAccountDetailsFormProps {
  data: DemoAccountDetails | WelcomeAccountDetails
  isEdit: boolean
  accountGroups: AccountGroupBasicDto[]
  leverageType: LeverageType[]
  tradingAccountStatus: NameDto[]
  tradingAccountRestrictions: RestrictionDto[]
  permissions: PermissionDto[]
  canEditTradingAccount: number
  canResetAccountPassword: number

  setEdit(value: boolean): void

  setAuditLogModal(value: boolean): void

  resetPassword({ withInvestorPassword }: { withInvestorPassword?: boolean }): void

  onSubmit(values: FormSubmitValues<UpdateDemoTradingAccount>): Promise<void>

  onOpenAddDeductFunds?(fundsType: AccountFundsType): void

  onDisqualify?(): void
}

export const DemoWelcomeAccountDetailsForm = withFormik<
  DemoWelcomeAccountDetailsFormProps,
  DemoWelcomeAccountDetailsFormValues
>({
  mapPropsToValues: ({ data }) => {
    return {
      name: data.name,
      accountGroupId: data.accountGroup.id,
      leverageType: data.leverage,
      statusTypeId: data.status.id,
      lead: data.lead,
      created: formatLocalDateUTC(data.created, true) || '',
      externalId: data.externalId ?? '',
      tickmillCompany: data.tickmillCompany.name,
      server: data.server.name,
      platformType: data.accountGroup.platformType.name,
      isTradingViewConnected: data.isTradingViewConnected,
      tradingAccountGroupType: data.accountGroup.tradingAccountType.name,
      accountGroupType: data.accountGroup.accountGroupType.name,
      bookType: data.accountGroup.bookType?.name,
      classification: data.accountGroup.classificationType.name,
      currency: data.accountGroup.currency.id,
      country: data.accountGroup.country?.name,
      isEnabled: data.accountGroup.isEnabled,
      isSwapFree: data.accountGroup.isSwapFree,
      canBeAttached: data.accountGroup.canBeAttached,
      canBeDirectAssigned: data.accountGroup.canBeDirectAssigned,
      canBeIBAssigned: data.accountGroup.canBeIBAssigned,
      accountGroupRestrictions: data.accountGroup.restrictions?.map((x) => x.id) || [],
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      setSubmitting(true)
      await props.onSubmit(dataToSubmit(values))
    } finally {
      setSubmitting(false)
    }
  },
  enableReinitialize: true,
})(DemoWelcomeAccountDetailsFormUI)
